import { isPilotVisible, type PilotContext } from '@runtime/pilot.js';
import type { Profile } from '@schema/profile.js';
import type { Tile } from '@schema/tiles.js';

/** Per-device pilot inputs, minus the per-tile `tileId` the filter adds. */
export type PilotDeviceContext = Omit<PilotContext, 'tileId'>;

/**
 * Drop tiles a pilot rule hides on THIS device, across every surface a
 * tile can appear (grid, tools, hero featured, and folder children).
 * Applied to the resolved active profile before render, so ProfileView
 * (and search, folders, featured) stay pilot-unaware.
 *
 * When the tenant isn't entitled to `pilotRollout`, returns the profile
 * untouched — pilots fail open to "no targeting", so a tile authored with
 * a pilot block still shows everywhere rather than vanishing fleet-wide.
 */
export function applyPilotFilter(
  profile: Profile,
  device: PilotDeviceContext,
  entitled: boolean,
  nowMs: number,
): Profile {
  if (!entitled) return profile;

  const keep = (tile: Tile): boolean => {
    const pilot = 'pilot' in tile ? tile.pilot : undefined;
    return pilot === undefined || isPilotVisible(pilot, { ...device, tileId: tile.id }, nowMs);
  };

  // Generic over the element type so each surface (grid / tools / featured)
  // gets its exact array type back, not a widened Tile[].
  const filterList = <T extends Tile>(tiles: readonly T[]): T[] => {
    const out: T[] = [];
    for (const tile of tiles) {
      if (!keep(tile)) continue;
      if (tile.type === 'folder') {
        const children = tile.children.filter(keep);
        // A folder whose children were all piloted away is an empty modal —
        // drop it rather than render a dead tile.
        if (children.length === 0) continue;
        // Unchanged → keep the original reference; otherwise rebuild with the
        // surviving children. The assertion only sidesteps
        // exactOptionalPropertyTypes widening optionals on the spread.
        out.push(children.length === tile.children.length ? tile : ({ ...tile, children } as T));
      } else {
        out.push(tile);
      }
    }
    return out;
  };

  return {
    ...profile,
    grid: { ...profile.grid, tiles: filterList(profile.grid.tiles) },
    tools: filterList(profile.tools),
    featuredTiles: filterList(profile.featuredTiles),
  };
}
